import { useDateTimeFormat } from '@core/shared';
import { WebinarAttachment, WebinarDataWatchLink, WebinarDataWithStatus } from '@core/swagger-client';
import { WebinarStatus } from '@core/types';
import { Attachment, CalendarAlt, Clock, Download, DurationIcon, Heading, Host, PlannedDurationIcon, TestWebinarBadge } from '@core/ui';
import { map } from 'lodash-es';
import { Duration } from 'luxon';
import { Trans, useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import { RelatedWebinars } from '../../atoms/RelatedWebinars';
import { WebinarInfoItem } from '../../molecules/WebinarInfoItem';

export const WebinarInfo = ({
    webinar,
    webinarAttachments
}: {
    webinar: WebinarDataWithStatus | WebinarDataWatchLink;
    webinarAttachments: WebinarAttachment[];
}) => {
    const { t } = useTranslation();

    const { date: broadcastDate, time: broadcastTime } = useDateTimeFormat(webinar.broadcastDateTime);

    const actualDuration = webinar.actualDuration ? Duration.fromISOTime(webinar.actualDuration) : Duration.fromMillis(0);
    const plannedDuration = Duration.fromISOTime(webinar.plannedDuration);

    return (
        <>
            <div className="relative flex menu:pl-4 md:pl-10">
                {webinar.assets.profileImage && (
                    <img
                        src={webinar.assets.profileImage.imageUrl}
                        className="absolute top-[calc(100%-1rem)] left-[calc(1rem-2px)] menu:relative menu:top-0 menu:left-0 size-20 md:size-30 rounded-webinarsub menu:-mb-4 outline outline-2 outline-gray-100 z-30"
                        alt={webinar.assets.profileImage.altText ?? ''}
                    />
                )}
                <div className="flex items-end min-w-0 menu:pl-4 md:pl-6 pb-4">
                    <Heading variant="h1">{webinar && webinar.title}</Heading>
                </div>
            </div>
            {webinar.assets.bannerImage && (
                <div className="relative min-h-32 flex z-20">
                    <img src={webinar.assets.bannerImage.imageUrl} className="min-h-0 rounded-webinarsub object-cover" alt="" />
                </div>
            )}

            <div
                className={`relative bg-accent-lighter rounded-b-webinarsub px-4 md:px-10 ${webinar.assets.bannerImage ? '-mt-2' : ''} ${webinar.assets.profileImage || webinar.assets.bannerImage ? 'pt-6 pb-4' : 'py-4'} z-10`}
            >
                {/*
                 * Viewer surfaces mark a test webinar with the badge only: the limits, the billing and the
                 * deletion moment in TestWebinarNotice are the host's concern, and a details or watch-link
                 * page can be reached from outside the company. The host keeps the full notice on the
                 * management list and the create/edit form.
                 */}
                {webinar.isTest && <TestWebinarBadge classNames="mb-3" />}
                <dl className="flex flex-col md:flex-row gap-2 md:gap-6">
                    <WebinarInfoItem icon={<CalendarAlt classNames="w-6 mr-1.5 stroke-current" />} label={t('WebinarDetails.Label.Date')}>
                        {broadcastDate}
                    </WebinarInfoItem>
                    {webinar.status !== WebinarStatus.Ended && (
                        <WebinarInfoItem icon={<Clock classNames="w-6 mr-1.5 stroke-current" />} label={t('WebinarDetails.Label.Time')}>
                            {broadcastTime}
                        </WebinarInfoItem>
                    )}
                    {webinar.status !== WebinarStatus.Ended ? (
                        <WebinarInfoItem icon={<PlannedDurationIcon classNames="w-6 mr-1.5 stroke-current" />} label={t('WebinarDetails.Label.Duration')}>
                            {plannedDuration.toFormat('h:mm')}
                        </WebinarInfoItem>
                    ) : (
                        <WebinarInfoItem icon={<DurationIcon classNames="w-6 mr-1.5 stroke-current" />} label={t('WebinarDetails.Label.Duration')}>
                            {actualDuration.toFormat('h:mm')}
                        </WebinarInfoItem>
                    )}
                </dl>
                <div className="flex mt-4 mb-2">
                    <Host classNames="w-6 mr-1.5 stroke-current" />
                    {t('WebinarDetails.Label.Host')}
                </div>
                <p className="md:text-lg font-medium leading-none">{webinar.nameOfHost}</p>
            </div>
            {(webinar.description || webinarAttachments?.length > 0) && (
                <div className="relative bg-white rounded-b-webinarsub shadow-[0_2rem_hsl(var(--c-accent-lighter))] -mt-2 mb-4 px-4 md:px-10 pt-6 pb-4">
                    <div className="prose">
                        <Markdown>{webinar.description}</Markdown>
                    </div>
                    {webinarAttachments?.length > 0 && (
                        <>
                            <Heading variant="h2" styleAs="h3" classNames="border-t border-t-gray-300 flex items-center mt-6 md:mt-10 mb-2 pt-6">
                                <Attachment classNames="size-6 me-1.5 stroke-current" />
                                {webinarAttachments.length === 1 ? (
                                    t('Content.Attachments.Heading.OneAttachment')
                                ) : (
                                    <Trans i18nKey="Content.Attachments.Heading.MultipleAttachments" values={{ amount: webinarAttachments.length }} />
                                )}
                            </Heading>
                            <ul className="flex flex-col items-start gap-y-4 mb-2">
                                {map(webinarAttachments, (attachment) => (
                                    <li key={attachment.attachmentId}>
                                        <a className="flex gap-4 bg-white px-4 py-2 shadow-md" href={attachment.path} target="_blank" rel="noopener noreferrer">
                                            {attachment.title} <Download classNames="size-6 stroke-primary" />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                    <RelatedWebinars webinar={webinar} />
                </div>
            )}
        </>
    );
};
